import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';

import { Communicators } from 'presentation/components/lib/communicators';
import { isMobileIos } from 'lib/getCustomerProperties';
import Datestamp, { formatDatestamp } from 'presentation/components/Datestamp';
import EndMessageContainer from 'presentation/containers/EndMessageContainer';
import FileMessageItemContainer from 'presentation/selfService/FileMessageItem';
import ImageMessageItemContainer from 'presentation/selfService/ImageMessageItem';
import Message from 'presentation/components/Message';
import messageEnums from 'enums/messages';
import MessageStack from 'presentation/components/MessageStack';
import overrideStyle from 'lib/overrideStyle';
import PaymentRequestMessageItemContainer from 'presentation/selfService/containers/PaymentRequestMessageItemContainer';
import { platforms } from 'enums/app';
import TypingIndicator from 'presentation/components/TypingIndicator';

import styles from 'presentation/components/styles/ChatHistory.css';
import InteractiveOnboardingContainer from 'presentation/selfService/containers/InteractiveOnboardingContainer';
import PreIdentifiedUserWelcomeMessageContainer from 'presentation/selfService/containers/PreIdentifiedUserWelcomeMessageContainer';

let cx = classnames.bind(styles);

class ChatHistory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isKeyboardOpen: false,
      shouldDisplayAutoResponse: this.props.messageHistory.length > 1,
    };
    this.scrollToBottom = this.scrollToBottom.bind(this);
  }

  componentDidUpdate(prevProps) {
    /* Scroll to bottom of chat history for the below events:
    1) new chat message
    2) chat session has ended
    3) agent typing indicators -- only when already scrolled to the bottom. Do not scroll for typing indicators when customer has scrolled up to view an older message
    */
    let isNewMessage = this.props.messageHistory.length > prevProps.messageHistory.length;
    let hasChatEnded = !prevProps.isChatEnded && this.props.isChatEnded;
    let isOnboardingMessage = this.props.messageHistory.length < 2;
    if (
      isNewMessage ||
      isOnboardingMessage ||
      hasChatEnded ||
      (this.props.isAgentTyping && this.isScrolledNearBottom())
    ) {
      this.scrollToBottom();
    }

    // if the first message has been sent, wait 1 second before displaying the autoresponse
    if (
      !this.state.shouldDisplayAutoResponse &&
      prevProps.messageHistory.length !== this.props.messageHistory.length &&
      this.props.messageHistory.length === 2
    ) {
      this.timeout = setTimeout(() => {
        this.setState({ shouldDisplayAutoResponse: true });
        this.scrollToBottom();
      }, 1000);
    }
  }

  componentDidMount() {
    /* Add a small delay to get the accurate scrollHeight of the containerElement. Without the delay, the scrollHeight can be a little off -- it won't quite be scrolled down all the way */
    setTimeout(() => this.scrollToBottom(), 100);

    /* Must keep track of lastChatHeight because the containerElement.clientHeight isn't always accurate. It can potentially be smaller before the keyboard animation finishes. lastChatHeight is only used to determine if a mobile device keyboard has opened or closed */
    this.lastChatHeight = this.containerElement.clientHeight;
    this.originalChatHeight = this.containerElement.clientHeight;
    if (this.props.platform === platforms.IOS) {
      window.addEventListener('resize', this.handleMobileKeyboard.bind(this));
    }
  }

  isScrolledNearBottom() {
    const bottomMargin = 100; // decrease to make this check more strict. 100 pixels gives a little leeway when determining if the user is scrolled at the bottom.
    return (
      this.containerElement.scrollHeight - this.containerElement.scrollTop - bottomMargin <=
      this.containerElement.clientHeight
    );
  }

  handleMobileKeyboard() {
    if (this.state.isKeyboardOpen) {
      this.setState({ isKeyboardOpen: false });
      /* Scroll to bottom of chat history if scrolled all the way down when closing the keyboard. This needs to be special cased for some unknown reason. Without this special case, closing the keyboard while scrolled all the way down the chat history will result in the timestamp being cut off from the view. This only applies to the very last timestamp in the chat history */
      if (this.containerElement.scrollTop + this.containerElement.clientHeight === this.containerElement.scrollHeight) {
        this.scrollToBottom();
      } else {
        /* If the bottom of the chat history is not scrolled all the way down when closing the keyboard, adjusting by the keyboard height works correctly */
        this.containerElement.scrollTop -= this.keyboardHeight;
      }
    } else {
      this.setState({ isKeyboardOpen: true });
      this.keyboardHeight = this.originalChatHeight - this.containerElement.clientHeight;
      this.containerElement.scrollTop += this.keyboardHeight;
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
    if (this.props.platform === platforms.IOS) {
      window.removeEventListener('resize', this.handleMobileKeyboard.bind(this));
    }
  }

  scrollToBottom() {
    if (!this.containerElement) {
      return;
    }
    this.containerElement.scrollTop = this.containerElement.scrollHeight;
  }

  renderMessageHistory() {
    let renderedHistory = [];
    let renderedMessages = [];
    let renderedDates = new Set();

    var shouldShowDate = (messageDate, currentDate) => {
      if (!this.props.isMobileSdk) {
        return false;
      }

      if (renderedDates.has(messageDate)) {
        return false;
      }
      // Do not show current date as datestamp unless there are older messages above it
      if (messageDate === currentDate && renderedDates.size === 0) {
        return false;
      }
      return true;
    };

    this.props.messageHistory.forEach((message, index, messages) => {
      let sender = getSender(message);
      let nextSender = getSender(messages[index + 1]);
      let nextMessage = messages[index + 1];
      let currentDate = formatDatestamp(new Date());
      let messageDate = formatDatestamp(message.date);
      let nextMessageDate = nextMessage && formatDatestamp(nextMessage.date);

      if (shouldShowDate(messageDate, currentDate)) {
        renderedHistory.push(<Datestamp timestamp={message.date} key={message.date} />);
        renderedDates.add(messageDate);
      }

      if (message.subType === messageEnums.SUB_TYPES.TEXT) {
        renderedMessages.push(<Message sender={sender} content={message.content.body} key={message.id} />);
      } else if (message.subType === messageEnums.SUB_TYPES.IMAGE) {
        renderedMessages.push(
          <ImageMessageItemContainer
            attachmentId={message.attachmentId}
            fileName={message.content.fileName}
            gladlyCustomerId={message.gladlyCustomerId}
            key={message.id}
            listId={message.id}
            sender={sender}
            src={message.content.mediaUrl}
            updateScrollPosition={this.scrollToBottom}
          />
        );
      } else if (message.subType === messageEnums.SUB_TYPES.FILE) {
        renderedMessages.push(
          <FileMessageItemContainer
            attachmentId={message.attachmentId}
            fileName={message.content.fileName}
            gladlyCustomerId={message.gladlyCustomerId}
            key={message.id}
            sender={sender}
            src={message.content.mediaUrl}
          />
        );
      } else if (message.subType === messageEnums.SUB_TYPES.CREDIT_CARD_REQUEST) {
        renderedMessages.push(
          <PaymentRequestMessageItemContainer
            currency={message.content.currency}
            gladlyCustomerId={message.gladlyCustomerId}
            paymentId={message.content.paymentId}
            key={message.id}
            memo={message.content.memo}
            monetaryUnit={message.content.monetaryUnit}
            sender={sender}
            status={message.content.status}
          />
        );
      }

      let shouldDisplay = message.id === 'auto-response' ? this.state.shouldDisplayAutoResponse : true;
      if (shouldDisplay && (sender !== nextSender || messageDate !== nextMessageDate || message._hasError)) {
        renderedHistory.push(
          <MessageStack
            key={index}
            timestamp={message.date}
            hasError={message._hasError}
            sender={sender}
            messages={renderedMessages}
          />
        );
        renderedMessages = [];
      }
    });
    return renderedHistory;
  }

  renderWelcomeMessageOrInteractiveOnboarding() {
    if (this.props.isPreIdentifiedUser) {
      return <PreIdentifiedUserWelcomeMessageContainer />;
    }
    return <InteractiveOnboardingContainer updateScrollPosition={this.scrollToBottom} />;
  }

  // get length of the rendered history and if its the agent is the last, when you render that the agent is typing, remove the timestamp

  render() {
    return (
      <div
        aria-live="polite"
        aria-relevant="additions"
        role="log"
        data-aid="gladlyChatHistoryList"
        className={cx(
          styles.chatHistory_wrapper,
          styles.chatHistory_wrapper_self_service,
          this.props.isMobileSdk ? styles.chatHistory_wrapper_borderless : null
        )}
        ref={div => (this.containerElement = div)}
      >
        <ChatHistoryWrapper
          tabIndex="-1"
          isSelfService={this.props.isSelfService}
          isMobileIos={isMobileIos()}
          isMobileSdk={this.props.isMobileSdk}
        >
          {this.renderWelcomeMessageOrInteractiveOnboarding()}
          {this.renderMessageHistory()}
          {this.props.isAgentTyping ? <TypingIndicator /> : null}
          {this.props.isChatEnded ? <EndMessageContainer /> : null}
        </ChatHistoryWrapper>
      </div>
    );
  }
}

ChatHistory.propTypes = {
  isAgentTyping: PropTypes.bool,
  isChatEnded: PropTypes.bool.isRequired,
  isMobileSdk: PropTypes.bool,
  messageHistory: PropTypes.array,
  platform: PropTypes.string,
};

function getSender(message) {
  if (!message) {
    return null;
  }
  return message.isUser ? Communicators.CUSTOMER : Communicators.AGENT;
}

export default ChatHistory;

/* mobile safari chat messages start from the bottom of the screen to avoid offscreen window issues */
const ChatHistoryWrapper = overrideStyle('div')`
  font-family: ${p => (p.theme.fontLibrary.secondary ? p.theme.fontLibrary.secondary : p.theme.fontLibrary.primary)};
  height: 100%;
  &:focus {
    border: none;
    outline: none;
  }

  @media only screen and (max-device-width: 667px), screen and (max-width: 599px) {
    height: ${p => (p.isMobileIos && !p.isMobileSdk ? 'auto' : '100%')};
    margin-top: ${p => (p.isMobileIos && !p.isMobileSdk ? 'auto' : 'inherit')};
    padding-top: ${p => (p.isMobileSdk ? '10px' : '0px')};
  }
`;
